import { useApi } from 'utils'
import * as yup from "yup"
import toast from 'react-hot-toast'
import { Controller, FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { AccountModal, ActionButton, BaseTable, Button, Card, HeaderMenu, Input, Loading, Modal, NumberInput, PaginationData, Select, TableNumber } from 'components'
import { useLocation, useNavigate } from 'react-router-dom'
import { FaHistory, FaPlus, FaRegSave } from 'react-icons/fa'
import { 
    useCategoryCustomer, 
    useCOAAsset, 
    useCOACogs, 
    useCOAExpences, 
    useCOAExpencesCogs, 
    useCOAIncome, 
    useProductCategory, 
    useProductColour, 
    useProductGroup, 
    useProductMaterial, 
    useProductSize, 
    useProductStyle, 
    useProductUnit, 
    useTax, 
    useVendor 
} from 'pages/Inventori/Transaction/AddNewItem/components/FormSection/utils'
import { CreateModal as PopupGroup } from "../../../../Setup/Inventory/ProductGroup/components"
import { CreateModal as PopupStyle } from "../../../../Setup/Inventory/ProductStyle/components"
import { CreateModal as PopupSize } from "../../../../Setup/Inventory/ProductSize/components"
import { CreateModal as PopupUnit } from "../../../../Setup/Inventory/ProductUnit/components"
import { CreateModal as PopupColour } from "../../../../Setup/Inventory/ProductColour/components"
import { CreateModal as PopupCategory } from "../../../../Setup/Inventory/ProductCategory/components"
import { CreateModal as PopupCustomer } from "../../../../Setup/Preference/CategoryCustomer/components"
import { CreateModal as PopupMaterial } from "../../../../Setup/Inventory/ProductMaterial/components"
import { CreatePage as PopupVendor } from "../../../../Card/Transaction/CreateNewVendor/components"
import { useEffect, useState } from 'react'
import { AddAccount } from 'pages/Inventori/Transaction/AddNewItem/components/FormSection/components'
import { useDetail } from '../utils'
import { StatusEnum } from '..'
import { PaginationState } from 'types'
import { ProductReview } from '../types'

const default_pagination_value = {
  total: 0,
  page: 1,
  limit: 50,
  pages: 1,
  keyword: "",
}

export const ReviewPage = () => {
  const navigate = useNavigate()
  const api = useApi()
  const location = useLocation()
  const dataPage = location?.state

  const [pagination, setPagination] = useState<PaginationState & { file_name?: string, file_status?: number }>({ ...default_pagination_value, file_name: dataPage?.file_name, file_status: dataPage?.status! })
  const dataSingle = useDetail(pagination)

  const [modalConfig, setModalConfig] = useState({ show: false, action: "", data: {} })
  const [checkedIndexes, setCheckedIndexes] = useState<number[]>([]);
  const productBSI = [
    { label: "Yes", value: "true" }, 
    { label: "No", value: "false" }
  ]

  const productGroup = useProductGroup()
  const productStyle = useProductStyle()
  const productSize = useProductSize()
  const productWeight = useProductUnit("name")
  const productColour = useProductColour()
  const productCategory = useProductCategory()
  const productMarket = useCategoryCustomer()
  const productMaterial = useProductMaterial()
  const productSell = useCOAIncome()
  const productInventory = useCOAAsset()
  const productVendor = useVendor()
  const productTax = useTax()
  const productCOGS = useCOACogs()
  const productExpencesCOGS = useCOAExpencesCogs()
  const productExpences = useCOAExpences()

  const isLoadingArray = [
    dataSingle?.isLoading,
    productGroup?.isLoadingProduct,
    productStyle?.isLoadingStyle,
    productSize?.isLoadingSize,
    productWeight?.isLoadingUnit,
    productColour?.isLoadingColour,
    productMarket?.isLoadingCustomer,
    productSell?.isLoading,
    productInventory?.isLoading,
    productVendor?.isLoadingVendor,
    productCategory?.isLoadingCategory,
    productMaterial?.isLoadingMaterial,
    productCOGS?.isLoading,
    productExpencesCOGS?.isLoading,
    productExpences?.isLoading,
  ];
  
  const tableLoading = isLoadingArray.some(Boolean);

  const validationSchema = yup.object().shape({ datavalue: yup.array().label("Data list").defined() })
  const methods = useForm<any>({
    defaultValues: { datavalue: "" },
    resolver: yupResolver(validationSchema)
  })
  const { fields } = useFieldArray({ control: methods.control, name: "datavalue" })
  const dataReview = useWatch({ control: methods.control, name: "datavalue" });

  const getSelectedData = () => {
    if(dataReview) {
      return dataReview?.filter((_: any, index: any) => checkedIndexes?.includes(index));
    }
  };
  const selectedData = getSelectedData();

  const sanitizeWholesale = (data: ProductReview) => {
    const sanitizeString = (value: string | undefined): string => {
      if (!value) return "";
      let sanitized = value.replace(/,/g, "");
      if (sanitized.endsWith(".00")) {
        sanitized = sanitized.slice(0, -3);
      }
  
      return sanitized;
    };

    return {
      ...data,
      main_size_id: Number(data.main_size_id),
      mr_category_customer_id: Number(data.mr_category_customer_id),
      mr_product_category_id: Number(data.mr_product_category_id),
      mr_product_colour_id: Number(data.mr_product_colour_id),
      mr_product_group_id: Number(data.mr_product_group_id),
      mr_product_material_id: Number(data.mr_product_material_id),
      mr_product_style_id: Number(data.mr_product_style_id),

      weight: sanitizeString(data.weight),
      buying_price: sanitizeString(data.buying_price),
      quantity_unit_convention: sanitizeString(data.quantity_unit_convention),
      over_head: sanitizeString(data.over_head),
      sale_tax: sanitizeString(data.sale_tax),
      other_change: sanitizeString(data.other_change),
      quantity_unit_sell_convention: sanitizeString(data.quantity_unit_sell_convention),
      price_retail: sanitizeString(data.price_retail),
      wholesale_price: sanitizeString(data.wholesale_price),
      distributor_price: sanitizeString(data.distributor_price),
      factory_price: sanitizeString(data.factory_price),
      online_price: sanitizeString(data.online_price),
      special_arrangement: sanitizeString(data?.special_arrangement)
    };
  }

  const sanitizeWholesaleMap = (data: ProductReview[]): ProductReview[] => {
    const sanitizeString = (value: string | undefined): string => {
      if (!value) return "";
      let sanitized = value.replace(/,/g, "");
      if (sanitized.endsWith(".00")) {
        sanitized = sanitized.slice(0, -3);
      }
  
      return sanitized;
    };
  
    return data.map((item) => ({
      ...item,
      main_size_id: Number(item.main_size_id),
      mr_category_customer_id: Number(item.mr_category_customer_id),
      mr_product_category_id: Number(item.mr_product_category_id),
      mr_product_colour_id: Number(item.mr_product_colour_id),
      mr_product_group_id: Number(item.mr_product_group_id),
      mr_product_material_id: Number(item.mr_product_material_id),
      mr_product_style_id: Number(item.mr_product_style_id),
  
      weight: sanitizeString(item.weight),
      buying_price: sanitizeString(item.buying_price),
      quantity_unit_convention: sanitizeString(item.quantity_unit_convention),
      over_head: sanitizeString(item.over_head),
      sale_tax: sanitizeString(item.sale_tax),
      other_change: sanitizeString(item.other_change),
      quantity_unit_sell_convention: sanitizeString(item.quantity_unit_sell_convention),
      price_retail: sanitizeString(item.price_retail),
      wholesale_price: sanitizeString(item.wholesale_price),
      distributor_price: sanitizeString(item.distributor_price),
      factory_price: sanitizeString(item.factory_price),
      online_price: sanitizeString(item.online_price),
      special_arrangement: sanitizeString(item?.special_arrangement),
    }));
  };

  const sanitizePayload = (data: Payload): Payload => {
    if (Array.isArray(data)) {
      return data.map(sanitizePayload).filter((item) => Object.keys(item).length > 0);
    } else if (data && typeof data === "object") {
      return Object.keys(data).reduce((acc, key) => {
        const value = data[key];
  
        if (value === null || value === undefined || value === "") {
          acc[key] = 0;
        } else {
          acc[key] = sanitizePayload(value);
        }
        
        return acc;
      }, {} as any);
    }
    
    return data;
  };  

  const handleCreateSingle = () => {
    return new Promise<void>((resolve) => {
      // Vars
      const allData = dataSingle.allData.map(item => {
        // Vars
        const displayedData = dataSingle.data.payload.find(i => i.preview_id === item.preview_id)

        if (displayedData) {
          return displayedData
        }

        return item
      })
      const sanitizedSelect = sanitizePayload(sanitizeWholesaleMap(selectedData));
      const sanitizedReview = sanitizePayload(sanitizeWholesaleMap(allData));
      const payloadMapper = selectedData?.length !== 0 ? sanitizedSelect : sanitizedReview

      toast.promise(
        api.post(`/importexcel/posting_product_import?file_name=${dataPage?.file_name}`, payloadMapper),
        {
          loading: "Loading...",
          success: (res) => res.data.message,
          error: (err) => err.response.data.message ?? err.response.message
        }
      )
      .then(() => {
        dataSingle.refetch()
        selectedData?.length === 0 && navigate(-1)
        setCheckedIndexes([])
      })
      .catch(() => {
        dataSingle.refetch()
        setCheckedIndexes([])
      })
      .finally(resolve)
    })
  }
  const handleUpdateSingle = (data: ProductReview) => {
    const sanitizedData = sanitizePayload(sanitizeWholesale(data));

    return new Promise<void>((resolve) => {
      toast.promise(
        api.post(`/importexcel/posting_product_import?file_name=${dataPage?.file_name}`, [sanitizedData]),
        {
          loading: "Loading...",
          success: (res) => res.data.message,
          error: (err) => err.response.data.message ?? err.response.message
        }
      )
      .then(() => dataSingle.refetch())
      .catch(() => dataSingle.refetch())
      .finally(resolve)
    })
  }

  const handleDeleteSingle = (id: number) => {
    return new Promise<void>((resolve) => {
      toast.promise(
        api.delete(`/importexcel/delete_single_row?preview_id=${id}`),
        {
          loading: "Loading...",
          success: (res) => res.data.message,
          error: (err) => err.response.data.message ?? err.response.message
        }
      )
      .then(() => dataSingle.refetch())
      .catch(() => dataSingle.refetch())
      .finally(resolve)
    })
  }
  const handleCheckboxChange = (index: number) => {
    setCheckedIndexes((prev) =>
      prev.includes(index)
        ? prev.filter((i) => i !== index)
        : [...prev, index]
    );
  };
  const handleCheckAllChange = () => {
    if (checkedIndexes.length === dataReview.length) {
      setCheckedIndexes([]);
    } else {
      setCheckedIndexes(dataReview.map((_: any, index: any) => index));
    }
  };

  const requiredProfile = [
    "product_barcode",
    // "product_name",
    // "mr_product_group_id",
    // "mr_product_style_id",
    // "main_size_id",
    // "weight",
    // "weight_unit_id",
    // "mr_product_colour_id",
    // "mr_product_category_id",
    // "mr_category_customer_id",
    // "mr_product_material_id",
  ];

  const inputClass = "my-[-4px]! mx-[-8px]!"
  const borderTH = "border-2 border-[#A6AEBF]"

  useEffect(() => {
    if (dataSingle?.data?.payload) {
      methods.reset({ datavalue: dataSingle?.data?.payload });
    }
    // eslint-disable-next-line
  }, [dataSingle?.data?.payload, methods.reset]);

  useEffect(() => {
    if (dataReview) {
      dataReview.forEach((item: any, index: number) => {
        if (!item.buy && !item.sell && !item.inventory) {
          methods.setValue(`datavalue.${index}.coa_buy`, null);
        }
        
        if (!item.sell) {
          methods.setValue(`datavalue.${index}.coa_sell`, null);
        }
        if (!item.inventory) {
          methods.setValue(`datavalue.${index}.coa_asset`, null);
        }
      });
    }
    // eslint-disable-next-line
  }, [JSON.stringify(dataReview), methods.setValue]);

  useEffect(() => {
    if (dataReview) {
      dataReview.forEach((item: any, index: number) => {
        if (!item.buy) {
          methods.setValue(`datavalue.${index}.vendor_id`, null);
          methods.setValue(`datavalue.${index}.buying_price`, "");
          methods.setValue(`datavalue.${index}.buy_tax_id`, null);
          methods.setValue(`datavalue.${index}.unit_measure_id`, null);
          methods.setValue(`datavalue.${index}.quantity_unit_convention`, "");
          methods.setValue(`datavalue.${index}.unit_convention_id`, null);
        }
      });
    }
    // eslint-disable-next-line
  }, [JSON.stringify(dataReview), methods.setValue]);

  useEffect(() => {
    if (dataReview) {
      dataReview.forEach((item: any, index: number) => {
        if (!item.sell) {
          methods.setValue(`datavalue.${index}.over_head`, "");
          methods.setValue(`datavalue.${index}.sale_tax`, "");
          methods.setValue(`datavalue.${index}.other_change`, "");
          methods.setValue(`datavalue.${index}.unit_sell_measure_id`, null);
          methods.setValue(`datavalue.${index}.quantity_unit_sell_convention`, "");
          methods.setValue(`datavalue.${index}.price_retail`, "");
          methods.setValue(`datavalue.${index}.wholesale_price`, "");
          methods.setValue(`datavalue.${index}.distributor_price`, "");
          methods.setValue(`datavalue.${index}.factory_price`, "");
          methods.setValue(`datavalue.${index}.online_price`, "");
          methods.setValue(`datavalue.${index}.special_arrangement`, "");
          methods.setValue(`datavalue.${index}.sell_tax_id`, null);
        }
      });
    }
    // eslint-disable-next-line
  }, [JSON.stringify(dataReview), methods.setValue]);

  if(dataSingle?.isLoading) {
    return (
      <section className="container pt-5  gap-5 flex flex-col">
        <HeaderMenu title="REVIEW PRODUCT IMPORT" />

        <Card>
          <Card.Body>
            <Loading loading={dataSingle?.isLoading} />
          </Card.Body>
        </Card>
      </section>
    )
  }

  return (
    <section className="container gap-5 flex flex-col">
      <HeaderMenu title="REVIEW PRODUCT IMPORT" />

      <FormProvider {...methods}>
        <div className="overflow-auto max-h-[500px]">
          <BaseTable>
            <thead className='bg-[#EEEEEE] sticky top-0 z-[1]'>
              <tr>
                <th className={borderTH} rowSpan={2}>NO</th>
                <th className={borderTH} colSpan={11}>PRODUCT DATA</th>
                <th className={borderTH} colSpan={13}>PRODUCT PROFILE</th>
                <th className={borderTH} colSpan={6}>PRODUCT BUYING</th>
                <th className={borderTH} colSpan={12}>PRODUCT SELLING</th>
                <th className={borderTH} rowSpan={2}>STATUS</th>
                {dataPage?.status === 1 && <th className={borderTH} rowSpan={2}>ACTION</th>}
                {dataPage?.status === 1 && <th className={borderTH} rowSpan={2}>
                  SUBMIT<br/>ITEMS
                  <div>
                    <input
                      type='checkbox'
                      className='w-[22px] h-[22px] checkbox checkbox-primary mt-2'
                      checked={checkedIndexes.length === dataReview.length}
                      onChange={handleCheckAllChange}
                      >
                      
                    </input>
                  </div>
                </th>}
              </tr>
              <tr>
                <th className={borderTH}>BARCODE*</th>
                <th className={borderTH}>DESCRIPTION*</th>
                <th className={borderTH}>GROUP</th>
                <th className={borderTH}>STYLE</th>
                <th className={borderTH}>SIZE</th>
                <th className={borderTH}>WEIGHT</th>
                <th className={borderTH}>UNIT WEIGHT</th>
                <th className={borderTH}>COLOUR</th>
                <th className={borderTH}>CATEGORY</th>
                <th className={borderTH}>MARKET SEGMENT</th>
                <th className={borderTH}>MATERIAL</th>
                <th className={borderTH}>BUILD</th>
                <th className={borderTH}>SHOP</th>
                <th className={borderTH}>SERVICE</th>
                <th className={borderTH}>ONLINE</th>
                <th className={borderTH}>I BUY*</th>
                <th className={borderTH}>I SELL*</th>
                <th className={borderTH}>I INVENTORY*</th>
                <th className={borderTH} colSpan={2}>ACC. BUY</th>
                <th className={borderTH} colSpan={2}>ACC. SELL</th>
                <th className={borderTH} colSpan={2}>ACC. INVENTORY</th>
                <th className={borderTH}>VENDOR</th>
                <th className={borderTH}>PRICE</th>
                <th className={borderTH}>TAX</th>
                <th className={borderTH}>BUYING UNIT</th>
                <th className={borderTH}>QTY CONVENTION</th>
                <th className={borderTH}>UNIT CONVENTION</th>
                <th className={borderTH}>OVER HEAD(%)</th>
                <th className={borderTH}>SALE TAX(%)</th>
                <th className={borderTH}>OTHERS(%)</th>
                <th className={borderTH}>SELLING UNIT</th>
                <th className={borderTH}>QTY CONVENTION</th>
                <th className={borderTH}>RETAIL</th>
                <th className={borderTH}>WHOLESALE</th>
                <th className={borderTH}>DISTRIBUTOR</th>
                <th className={borderTH}>FACTORY</th>
                <th className={borderTH}>ONLINE</th>
                <th className={borderTH}>SPECIAL ARANGEMENT</th>
                <th className={borderTH}>TAX WHEN SOLD (%)</th>
              </tr>
            </thead>
            <tbody>
              {tableLoading ? <tr><td colSpan={44}><div className='flex justify-start ml-12'><Loading loading /></div></td></tr> : fields?.length === 0
                ? <tr><td className="text-center" colSpan={44}>No Data Available</td></tr>
                : fields.map((field, key) => {
                  const previewId = dataReview?.[key]?.preview_id
                  const idexValue = dataReview?.[key]

                  const isBuild = dataReview?.[key]?.build
                  const isBuy = dataReview?.[key]?.buy
                  const isSell = dataReview?.[key]?.sell
                  const isInventory = dataReview?.[key]?.inventory

                  const accBuy = dataReview?.[key]?.coa_buy
                  const accSell = dataReview?.[key]?.coa_sell
                  const accInventory = dataReview?.[key]?.coa_asset
                  const buyUnitConvention = dataReview?.[key]?.unit_convention_id
                  const status = dataReview?.[key]?.status

                  const isProfileDisable = !requiredProfile.every((field) => dataReview?.[key]?.[field]);
                  const isBuyingDisable = !isBuy
                  const isSellingDisable = !isSell

                  return (
                    <tr key={field.id}>
                      <td className={borderTH}>{TableNumber({ dataLength: dataSingle?.data?.pagination_data?.size, pageCurrent: pagination?.page, index: key, limit: pagination.limit, totalData: pagination?.total })}</td>

                      {/* BARCODE */}
                      <td className={borderTH}>
                        <Controller
                          control={methods.control}
                          name={`datavalue.${key}.product_barcode`}
                          render={({ field, fieldState }) => (
                            <Input 
                              {...field}
                              className={`${inputClass} w-[150px]`} 
                              placeholder="-"
                              onChange={(e) => field.onChange(e.target.value)}
                              error={fieldState.error?.message}
                            />
                          )}
                        />
                      </td>
                      
                      {/* DESCRIPTION */}
                      <td className={borderTH}>
                        <Controller
                          control={methods.control}
                          name={`datavalue.${key}.product_name`}
                          render={({ field, fieldState }) => (
                            <Input 
                              {...field}
                              className={`${inputClass} w-[300px]`} 
                              placeholder="-"
                              onChange={(e) => field.onChange(e.target.value)}
                              error={fieldState.error?.message}
                            />
                          )}
                        />
                      </td>

                      {/* PRODUCT GROUP */}
                      <td className={borderTH}>
                        <Controller
                          control={methods.control}
                          name={`datavalue.${key}.mr_product_group_id`}
                          render={({ field, fieldState }) => (
                            <div className="flex items-top">
                              <Select 
                                controlClass={`${inputClass} w-[200px] !rounded-r-none`} 
                                placeholder="Select Item Group"
                                isLoading={productGroup?.isLoadingProduct}
                                options={productGroup?.dataProduct}
                                value={field.value ? productGroup?.dataProduct.find((item) => item.value?.toString() === field.value?.toString()) : null}
                                onChange={(e) => field.onChange(e?.value)}
                                error={fieldState.error?.message}
                              />
                              <Button
                                type="button"
                                color="primary"
                                className="!rounded-r-lg"
                                onClick={() => setModalConfig({ ...modalConfig, show: true, action: "GROUP" })}
                              >
                                <FaPlus />
                              </Button>
                            </div>
                          )}
                        />
                      </td>

                      {/* PRODUCT STYLE */}
                      <td className={borderTH}>
                        <Controller
                          control={methods.control}
                          name={`datavalue.${key}.mr_product_style_id`}
                          render={({ field, fieldState }) => (
                            <div className="flex items-top">
                              <Select 
                                controlClass={`${inputClass} w-[200px] !rounded-r-none`} 
                                placeholder="Select Item Style"
                                isLoading={productStyle?.isLoadingStyle}
                                options={productStyle?.dataStyle}
                                value={field.value ? productStyle?.dataStyle.find((item) => item.value?.toString() === field.value?.toString()) : null}
                                onChange={(e) => field.onChange(e?.value)}
                                error={fieldState.error?.message}
                              />
                              <Button
                                type="button"
                                color="primary"
                                className="!rounded-r-lg"
                                onClick={() => setModalConfig({ ...modalConfig, show: true, action: "STYLE" })}
                              >
                                <FaPlus />
                              </Button>
                            </div>
                          )}
                        />
                      </td>

                      {/* PRODUCT SIZE */}
                      <td className={borderTH}>
                        <Controller
                          control={methods.control}
                          name={`datavalue.${key}.main_size_id`}
                          render={({ field, fieldState }) => (
                            <div className="flex items-top">
                              <Select 
                                controlClass={`${inputClass} w-[200px] !rounded-r-none`} 
                                placeholder="Select Item Size"
                                isLoading={productSize?.isLoadingSize}
                                options={productSize?.dataSize}
                                value={field.value ? productSize?.dataSize.find((item) => item.value?.toString() === field.value?.toString()) : null}
                                onChange={(e) => field.onChange(e?.value)}
                                error={fieldState.error?.message}
                              />
                              <Button
                                type="button"
                                color="primary"
                                className="!rounded-r-lg"
                                onClick={() => setModalConfig({ ...modalConfig, show: true, action: "SIZE" })}
                              >
                                <FaPlus />
                              </Button>
                            </div>
                          )}
                        />
                      </td>

                      {/* WEIGHT */}
                      <td className={borderTH}>
                        <Controller
                          control={methods.control}
                          name={`datavalue.${key}.weight`}
                          render={({ field, fieldState }) => (
                            <NumberInput 
                              {...field}
                              decimalSeparator="." 
                              thousandSeparator=","
                              className={`${inputClass} w-[100px]`} 
                              placeholder="-"
                              onValueChange={(e) => field.onChange(e.floatValue)}
                              error={fieldState.error?.message}
                            />
                          )}
                        />
                      </td>

                      {/* PRODUCT UNIT WEIGHT */}
                      <td className={borderTH}>
                        <Controller
                          control={methods.control}
                          name={`datavalue.${key}.weight_unit_id`}
                          render={({ field, fieldState }) => (
                            <div className="flex items-top">
                              <Select 
                                controlClass={`${inputClass} w-[200px] !rounded-r-none`} 
                                placeholder="Select Item Weight"
                                isLoading={productWeight?.isLoadingUnit}
                                options={productWeight?.dataUnit}
                                value={field.value ? productWeight?.dataUnit.find((item) => item.value?.toString() === field.value?.toString()) : null}
                                onChange={(e) => field.onChange(e?.value)}
                                error={fieldState.error?.message}
                              />
                              <Button
                                type="button"
                                color="primary"
                                className="!rounded-r-lg"
                                onClick={() => setModalConfig({ ...modalConfig, show: true, action: "WEIGHT" })}
                              >
                                <FaPlus />
                              </Button>
                            </div>
                          )}
                        />
                      </td>

                      {/* PRODUCT COLOUR */}
                      <td className={borderTH}>
                        <Controller
                          control={methods.control}
                          name={`datavalue.${key}.mr_product_colour_id`}
                          render={({ field, fieldState }) => (
                            <div className="flex items-top">
                              <Select 
                                controlClass={`${inputClass} w-[200px] !rounded-r-none`} 
                                placeholder="Select Item Colour"
                                isLoading={productColour?.isLoadingColour}
                                options={productColour?.dataColour}
                                value={field.value ? productColour?.dataColour.find((item) => item.value?.toString() === field.value?.toString()) : null}
                                onChange={(e) => field.onChange(e?.value)}
                                error={fieldState.error?.message}
                              />
                              <Button
                                type="button"
                                color="primary"
                                className="!rounded-r-lg"
                                onClick={() => setModalConfig({ ...modalConfig, show: true, action: "COLOUR" })}
                              >
                                <FaPlus />
                              </Button>
                            </div>
                          )}
                        />
                      </td>

                      {/* PRODUCT CATEGORY */}
                      <td className={borderTH}>
                        <Controller
                          control={methods.control}
                          name={`datavalue.${key}.mr_product_category_id`}
                          render={({ field, fieldState }) => (
                            <div className="flex items-top">
                              <Select 
                                controlClass={`${inputClass} w-[200px] !rounded-r-none`} 
                                placeholder="Select Item Category"
                                isLoading={productCategory?.isLoadingCategory}
                                options={productCategory?.dataCategory}
                                value={field.value ? productCategory?.dataCategory.find((item) => item.value?.toString() === field.value?.toString()) : null}
                                onChange={(e) => field.onChange(e?.value)}
                                error={fieldState.error?.message}
                              />
                              <Button
                                type="button"
                                color="primary"
                                className="!rounded-r-lg"
                                onClick={() => setModalConfig({ ...modalConfig, show: true, action: "CATEGORY" })}
                              >
                                <FaPlus />
                              </Button>
                            </div>
                          )}
                        />
                      </td>

                      {/* PRODUCT MARKET SEGMENT */}
                      <td className={borderTH}>
                        <Controller
                          control={methods.control}
                          name={`datavalue.${key}.mr_category_customer_id`}
                          render={({ field, fieldState }) => (
                            <div className="flex items-top">
                              <Select 
                                controlClass={`${inputClass} w-[200px] !rounded-r-none`} 
                                placeholder="Select Item Market"
                                isLoading={productMarket?.isLoadingCustomer}
                                options={productMarket?.dataCustomer}
                                value={field.value ? productMarket?.dataCustomer.find((item) => item.value?.toString() === field.value?.toString()) : null}
                                onChange={(e) => field.onChange(e?.value)}
                                error={fieldState.error?.message}
                              />
                              <Button
                                type="button"
                                color="primary"
                                className="!rounded-r-lg"
                                onClick={() => setModalConfig({ ...modalConfig, show: true, action: "MARKET" })}
                              >
                                <FaPlus />
                              </Button>
                            </div>
                          )}
                        />
                      </td>

                      {/* PRODUCT MATERIAL */}
                      <td className={borderTH}>
                        <Controller
                          control={methods.control}
                          name={`datavalue.${key}.mr_product_material_id`}
                          render={({ field, fieldState }) => (
                            <div className="flex items-top">
                              <Select 
                                controlClass={`${inputClass} w-[200px] !rounded-r-none`} 
                                placeholder="Select Item Material"
                                isLoading={productMaterial?.isLoadingMaterial}
                                options={productMaterial?.dataMaterial}
                                value={field.value ? productMaterial?.dataMaterial.find((item) => item.value?.toString() === field.value?.toString()) : null}
                                onChange={(e) => field.onChange(e?.value)}
                                error={fieldState.error?.message}
                              />
                              <Button
                                type="button"
                                color="primary"
                                className="!rounded-r-lg"
                                onClick={() => setModalConfig({ ...modalConfig, show: true, action: "MATERIAL" })}
                              >
                                <FaPlus />
                              </Button>
                            </div>
                          )}
                        />
                      </td>

                      {/* PRODUCT BUILD */}
                      <td className={borderTH}>
                        <Controller
                          control={methods.control}
                          name={`datavalue.${key}.build`}
                          render={({ field, fieldState }) => (
                            <div className="flex items-top">
                              <Select 
                                controlClass={`${inputClass} w-[100px]`} 
                                placeholder="Select"
                                isLoading={productMaterial?.isLoadingMaterial}
                                options={productBSI}
                                value={
                                  field.value !== undefined && field.value !== null
                                    ? productBSI.find((item) => item.value === String(field.value))
                                    : null
                                }
                                onChange={(e) => field.onChange(e?.value === "true")}
                                error={fieldState.error?.message}
                                isDisabled={isProfileDisable}
                              />
                            </div>
                          )}
                        />
                      </td>

                      {/* PRODUCT SHOP */}
                      <td className={borderTH}>
                        <Controller
                          control={methods.control}
                          name={`datavalue.${key}.shop`}
                          render={({ field, fieldState }) => (
                            <div className="flex items-top">
                              <Select 
                                controlClass={`${inputClass} w-[100px]`} 
                                placeholder="Select"
                                isLoading={productMaterial?.isLoadingMaterial}
                                options={productBSI}
                                value={
                                  field.value !== undefined && field.value !== null
                                    ? productBSI.find((item) => item.value === String(field.value))
                                    : null
                                }
                                onChange={(e) => field.onChange(e?.value === "true")}
                                error={fieldState.error?.message}
                                isDisabled={isProfileDisable}
                              />
                            </div>
                          )}
                        />
                      </td>

                      {/* PRODUCT SERVICE */}
                      <td className={borderTH}>
                        <Controller
                          control={methods.control}
                          name={`datavalue.${key}.service`}
                          render={({ field, fieldState }) => (
                            <div className="flex items-top">
                              <Select 
                                controlClass={`${inputClass} w-[100px]`} 
                                placeholder="Select"
                                isLoading={productMaterial?.isLoadingMaterial}
                                options={productBSI}
                                value={
                                  field.value !== undefined && field.value !== null
                                    ? productBSI.find((item) => item.value === String(field.value))
                                    : null
                                }
                                onChange={(e) => field.onChange(e?.value === "true")}
                                error={fieldState.error?.message}
                                isDisabled={isProfileDisable}
                              />
                            </div>
                          )}
                        />
                      </td>

                      {/* PRODUCT ONLINE DESCRIPTION */}
                      <td className={borderTH}>
                        <Controller
                          control={methods.control}
                          name={`datavalue.${key}.on_line`}
                          render={({ field, fieldState }) => (
                            <Input 
                              {...field}
                              className={`${inputClass} w-[300px]`} 
                              placeholder="-"
                              onChange={(e) => field.onChange(e.target.value)}
                              error={fieldState.error?.message}
                              disabled={isProfileDisable}
                            />
                          )}
                        />
                      </td>

                      {/* PRODUCT BUY */}
                      <td className={borderTH}>
                        <Controller
                          control={methods.control}
                          name={`datavalue.${key}.buy`}
                          render={({ field, fieldState }) => (
                            <div className="flex items-top">
                              <Select 
                                controlClass={`${inputClass} w-[100px]`} 
                                placeholder="Select Item Buy"
                                isLoading={productMaterial?.isLoadingMaterial}
                                options={productBSI}
                                value={
                                  field.value !== undefined && field.value !== null
                                    ? productBSI.find((item) => item.value === String(field.value))
                                    : null
                                }
                                onChange={(e) => {
                                  field.onChange(e?.value === "true")
                                  methods.setValue(`datavalue.${key}.coa_buy`, null)
                                }}
                                error={fieldState.error?.message}
                                isDisabled={isBuild || isProfileDisable}
                              />
                            </div>
                          )}
                        />
                      </td>

                      {/* PRODUCT SELL */}
                      <td className={borderTH}>
                        <Controller
                          control={methods.control}
                          name={`datavalue.${key}.sell`}
                          render={({ field, fieldState }) => (
                            <div className="flex items-top">
                              <Select 
                                controlClass={`${inputClass} w-[100px]`} 
                                placeholder="Select Item Sell"
                                isLoading={productMaterial?.isLoadingMaterial}
                                options={productBSI}
                                value={
                                  field.value !== undefined && field.value !== null
                                    ? productBSI.find((item) => item.value === String(field.value))
                                    : null
                                }
                                onChange={(e) => {
                                  field.onChange(e?.value === "true")
                                  methods.setValue(`datavalue.${key}.coa_sell`, null)
                                  // methods.setValue(`datavalue.${key}.coa_buy`, null)
                                }}
                                error={fieldState.error?.message}
                                isDisabled={isProfileDisable}
                              />
                            </div>
                          )}
                        />
                      </td>

                      {/* PRODUCT INVENTORY */}
                      <td className={borderTH}>
                        <Controller
                          control={methods.control}
                          name={`datavalue.${key}.inventory`}
                          render={({ field, fieldState }) => (
                            <div className="flex items-top">
                              <Select 
                                controlClass={`${inputClass} w-[100px]`} 
                                placeholder="Select Item Inventory"
                                isLoading={productMaterial?.isLoadingMaterial}
                                options={productBSI}
                                value={
                                  field.value !== undefined && field.value !== null
                                    ? productBSI.find((item) => item.value === String(field.value))
                                    : null
                                }
                                onChange={(e) => {
                                  field.onChange(e?.value === "true")
                                  methods.setValue(`datavalue.${key}.coa_asset`, null)
                                  // methods.setValue(`datavalue.${key}.coa_buy`, null)
                                }}
                                error={fieldState.error?.message}
                                isDisabled={isProfileDisable}
                              />
                            </div>
                          )}
                        />
                      </td>

                      {/* ACC. BUY */}
                      <td className={borderTH}>
                        <Controller
                          control={methods.control}
                          name={`datavalue.${key}.coa_buy`}
                          render={({ field, fieldState }) => (
                            <div className="flex items-top">
                              {isBuy && isSell && isInventory
                                ? (
                                  <>
                                    <Select 
                                      controlClass={`${inputClass} ${isProfileDisable ? "w-[290px]" : "w-[200px] !rounded-r-none"}`} 
                                      placeholder="Select COGS"
                                      isLoading={productCOGS?.isLoading}
                                      options={productCOGS?.data}
                                      value={field.value ? productCOGS?.data.find((item) => item.value?.toString() === field.value?.toString()) : null}
                                      onChange={(e) => field.onChange(e?.value)}
                                      error={fieldState.error?.message}
                                      isDisabled={isProfileDisable}
                                    />
                                    <AddAccount
                                      defaultGroup="05"
                                      noMargin
                                      isDetail={isProfileDisable}
                                      onSuccess={productCOGS?.refetch}
                                      refetch={productCOGS?.refetch}
                                    />
                                  </>)
                                : isBuy && isSell
                                  ? (
                                    <>
                                      <Select 
                                        controlClass={`${inputClass} w-[200px] !rounded-r-none`} 
                                        placeholder="Select coa expences and cogs"
                                        isLoading={productExpencesCOGS?.isLoading}
                                        options={productExpencesCOGS?.data}
                                        value={field.value ? productExpencesCOGS?.data.find((item) => item.value?.toString() === field.value?.toString()) : null}
                                        onChange={(e) => field.onChange(e?.value)}
                                        error={fieldState.error?.message}
                                        isDisabled={isProfileDisable}
                                      />
                                      <AddAccount
                                        defaultGroup=""
                                        noMargin
                                        isDetail={isProfileDisable}
                                        onSuccess={productExpencesCOGS?.refetch}
                                        refetch={productExpencesCOGS?.refetch}
                                      />
                                    </>)
                                  : isBuy && !isInventory
                                    ? (
                                        <>
                                          <Select 
                                            controlClass={`${inputClass} w-[200px] !rounded-r-none`} 
                                            placeholder="Select coa expences"
                                            isLoading={productExpences?.isLoading}
                                            options={productExpences?.data}
                                            value={field.value ? productExpences?.data.find((item) => item.value?.toString() === field.value?.toString()) : null}
                                            onChange={(e) => field.onChange(e?.value)}
                                            error={fieldState.error?.message}
                                            isDisabled={isProfileDisable}
                                          />
                                          <div className='flex'>
                                            <Button
                                              type="button"
                                              color="primary"
                                              onClick={productExpences?.refetch}
                                              disabled={isProfileDisable}
                                            >
                                              <FaHistory />
                                
                                            </Button>
                                            <Button
                                              type="button"
                                              color="primary"
                                              className="!rounded-r-lg"
                                              onClick={() => navigate("/accounts/register")}
                                            >
                                              <FaPlus />
                                            </Button>
                                          </div>
                                        </>
                                      )
                                    : isSell && isInventory
                                      ? (
                                        <>
                                          <Select 
                                            controlClass={`${inputClass} w-[200px] !rounded-r-none`} 
                                            placeholder="Select COGS"
                                            isLoading={productCOGS?.isLoading}
                                            options={productCOGS?.data}
                                            value={field.value ? productCOGS?.data.find((item) => item.value?.toString() === field.value?.toString()) : null}
                                            onChange={(e) => field.onChange(e?.value)}
                                            error={fieldState.error?.message}
                                            isDisabled={isProfileDisable}
                                          />
                                          <AddAccount
                                            defaultGroup="05"
                                            noMargin
                                            isDetail={isProfileDisable}
                                            onSuccess={productCOGS?.refetch}
                                            refetch={productCOGS?.refetch}
                                          />
                                        </>
                                      )
                                      : <>
                                          <Select 
                                            controlClass={`${inputClass} w-[290px]`} 
                                            placeholder="-"
                                            value={null}
                                            isDisabled
                                          />
                                        </>
                              }
                            </div>
                          )}
                        />
                      </td>

                      <td className={`${borderTH} text-nowrap`}>{
                        accBuy 
                          ? isBuy && isSell && isInventory
                            ? productCOGS?.data.find((item: any) => item.value?.toString() === accBuy?.toString())?.label?.replace(/^\d+-\d+\s*/, "")
                            : isBuy && isSell 
                              ? productExpencesCOGS?.data.find((item: any) => item.value?.toString() === accBuy?.toString())?.label?.replace(/^\d+-\d+\s*/, "")
                              : isBuy && !isInventory
                                ? productExpences?.data.find((item: any) => item.value?.toString() === accBuy?.toString())?.label?.replace(/^\d+-\d+\s*/, "")
                                : isSell && isInventory
                                  ? productCOGS?.data.find((item: any) => item.value?.toString() === accBuy?.toString())?.label?.replace(/^\d+-\d+\s*/, "")
                                  : "-"
                          : "-"
                      }</td>

                      {/* ACC. SELL */}
                      <td className={borderTH}>
                        <Controller
                          control={methods.control}
                          name={`datavalue.${key}.coa_sell`}
                          render={({ field, fieldState }) => (
                            <div className="flex items-top">
                              <Select 
                                controlClass={`${inputClass} w-[200px] !rounded-r-none`} 
                                placeholder="Select Acc Sell"
                                isLoading={productSell?.isLoading}
                                options={productSell?.data}
                                value={field.value ? productSell?.data.find((item) => item.value?.toString() === field.value?.toString()) : null}
                                onChange={(e) => field.onChange(e?.value)}
                                error={fieldState.error?.message}
                                isDisabled={!isSell || isProfileDisable}
                              />
                              <Button
                                type="button"
                                color="primary"
                                className="!rounded-r-lg"
                                onClick={() => setModalConfig({ ...modalConfig, show: true, action: "SELL" })}
                                disabled={!isSell || isProfileDisable}
                              >
                                <FaPlus />
                              </Button>
                            </div>
                          )}
                        />
                      </td>

                      <td className={`${borderTH} text-nowrap`}>{accSell ? productSell?.data.find((item: any) => item.value?.toString() === accSell?.toString())?.label?.replace(/^\d+-\d+\s*/, "") : "-"}</td>

                      {/* ACC. INVEMTORY */}
                      <td className={borderTH}>
                        <Controller
                          control={methods.control}
                          name={`datavalue.${key}.coa_asset`}
                          render={({ field, fieldState }) => (
                            <div className="flex items-top">
                              <Select 
                                controlClass={`${inputClass} w-[200px] !rounded-r-none`} 
                                placeholder="Select Acc Inventory"
                                isLoading={productInventory?.isLoading}
                                options={productInventory?.data}
                                value={field.value ? productInventory?.data.find((item) => item.value?.toString() === field.value?.toString()) : null}
                                onChange={(e) => field.onChange(e?.value)}
                                error={fieldState.error?.message}
                                isDisabled={!isInventory || isProfileDisable}
                              />
                              <Button
                                type="button"
                                color="primary"
                                className="!rounded-r-lg"
                                onClick={() => setModalConfig({ ...modalConfig, show: true, action: "INVENTORY" })}
                                disabled={!isInventory || isProfileDisable}
                              >
                                <FaPlus />
                              </Button>
                            </div>
                          )}
                        />
                      </td>

                      <td className={`${borderTH} text-nowrap`}>{accInventory ? productInventory?.data.find((item: any) => item.value?.toString() === accInventory?.toString())?.label?.replace(/^\d+-\d+\s*/, "") : "-"}</td>

                      {/* PRODUCT VENDOR */}
                      <td className={borderTH}>
                        <Controller
                          control={methods.control}
                          name={`datavalue.${key}.vendor_id`}
                          render={({ field, fieldState }) => (
                            <div className="flex items-top">
                              <Select 
                                controlClass={`${inputClass} w-[200px] !rounded-r-none`} 
                                placeholder="Select Item Vendor"
                                isLoading={productVendor?.isLoadingVendor}
                                options={productVendor?.dataVendor}
                                value={field.value ? productVendor?.dataVendor.find((item) => item.value?.toString() === field.value?.toString()) : null}
                                onChange={(e) => field.onChange(e?.value)}
                                error={fieldState.error?.message}
                                isDisabled={isBuyingDisable}
                              />
                              <Button
                                type="button"
                                color="primary"
                                className="!rounded-r-lg"
                                onClick={() => setModalConfig({ ...modalConfig, show: true, action: "VENDOR" })}
                                disabled={isBuyingDisable}
                              >
                                <FaPlus />
                              </Button>
                            </div>
                          )}
                        />
                      </td>
                      
                      {/* PRICE */}
                      <td className={borderTH}>
                        <Controller
                          control={methods.control}
                          name={`datavalue.${key}.buying_price`}
                          render={({ field, fieldState }) => (
                            <NumberInput 
                              {...field}
                              decimalSeparator="." 
                              thousandSeparator=","
                              className={`${inputClass} w-[100px]`} 
                              placeholder="-"
                              onValueChange={(e) => field.onChange(e.floatValue)}
                              error={fieldState.error?.message}
                              disabled={isBuyingDisable}
                            />
                          )}
                        />
                      </td>

                      {/* PRODUCT TAX */}
                      <td className={borderTH}>
                        <Controller
                          control={methods.control}
                          name={`datavalue.${key}.buy_tax_id`}
                          render={({ field, fieldState }) => (
                            <Select 
                              controlClass={`${inputClass} w-[200px]`} 
                              placeholder="Select Item Tax"
                              isLoading={productTax?.isLoadingTax}
                              options={productTax?.dataTax}
                              value={field.value ? productTax?.dataTax.find((item) => item.value?.toString() === field.value?.toString()) : null}
                              onChange={(e) => field.onChange(e?.value)}
                              error={fieldState.error?.message}
                              isDisabled={isBuyingDisable}
                            />
                          )}
                        />
                      </td>
                      
                      {/* PRODUCT BUYING UNIT */}
                      <td className={borderTH}>
                        <Controller
                          control={methods.control}
                          name={`datavalue.${key}.unit_measure_id`}
                          render={({ field, fieldState }) => (
                            <div className="flex items-top">
                              <Select 
                                controlClass={`${inputClass} w-[200px] !rounded-r-none`} 
                                placeholder="Select Item Unit"
                                isLoading={productWeight?.isLoadingUnit}
                                options={productWeight?.dataUnit}
                                value={field.value ? productWeight?.dataUnit.find((item) => item.value?.toString() === field.value?.toString()) : null}
                                onChange={(e) => field.onChange(e?.value)}
                                error={fieldState.error?.message}
                                isDisabled={isBuyingDisable}
                              />
                              <Button
                                type="button"
                                color="primary"
                                className="!rounded-r-lg"
                                onClick={() => setModalConfig({ ...modalConfig, show: true, action: "WEIGHT" })}
                                disabled={isBuyingDisable}
                              >
                                <FaPlus />
                              </Button>
                            </div>
                          )}
                        />
                      </td>

                      {/* QTY CONVENTION */}
                      <td className={borderTH}>
                        <Controller
                          control={methods.control}
                          name={`datavalue.${key}.quantity_unit_convention`}
                          render={({ field, fieldState }) => (
                            <NumberInput 
                              {...field}
                              decimalSeparator="." 
                              thousandSeparator=","
                              className={`${inputClass} w-[100px]`} 
                              placeholder="-"
                              onValueChange={(e) => field.onChange(e.floatValue)}
                              error={fieldState.error?.message}
                              disabled={isBuyingDisable}
                            />
                          )}
                        />
                      </td>
                      
                      {/* PRODUCT UNIT CONVENTION */}
                      <td className={borderTH}>
                        <Controller
                          control={methods.control}
                          name={`datavalue.${key}.unit_convention_id`}
                          render={({ field, fieldState }) => (
                            <div className="flex items-top">
                              <Select 
                                controlClass={`${inputClass} w-[200px] !rounded-r-none`} 
                                placeholder="Select Item Unit"
                                isLoading={productWeight?.isLoadingUnit}
                                options={productWeight?.dataUnit}
                                value={field.value ? productWeight?.dataUnit.find((item) => item.value?.toString() === field.value?.toString()) : null}
                                onChange={(e) => field.onChange(e?.value)}
                                error={fieldState.error?.message}
                                isDisabled={isBuyingDisable}
                              />
                              <Button
                                type="button"
                                color="primary"
                                className="!rounded-r-lg"
                                onClick={() => setModalConfig({ ...modalConfig, show: true, action: "WEIGHT" })}
                                disabled={isBuyingDisable}
                              >
                                <FaPlus />
                              </Button>
                            </div>
                          )}
                        />
                      </td>

                      {/* OVERHEAD */}
                      <td className={borderTH}>
                        <Controller
                          control={methods.control}
                          name={`datavalue.${key}.over_head`}
                          render={({ field, fieldState }) => (
                            <NumberInput 
                              {...field}
                              decimalSeparator="." 
                              thousandSeparator=","
                              className={`${inputClass} w-[150px]`} 
                              placeholder="-"
                              onValueChange={(e) => field.onChange(e.floatValue)}
                              error={fieldState.error?.message}
                              disabled={isSellingDisable}
                            />
                          )}
                        />
                      </td>

                      {/* SALE TAX */}
                      <td className={borderTH}>
                        <Controller
                          control={methods.control}
                          name={`datavalue.${key}.sale_tax`}
                          render={({ field, fieldState }) => (
                            <NumberInput 
                              {...field}
                              decimalSeparator="." 
                              thousandSeparator=","
                              className={`${inputClass} w-[150px]`} 
                              placeholder="-"
                              onValueChange={(e) => field.onChange(e.floatValue)}
                              error={fieldState.error?.message}
                              disabled={isSellingDisable}
                            />
                          )}
                        />
                      </td>

                      {/* OTHER */}
                      <td className={borderTH}>
                        <Controller
                          control={methods.control}
                          name={`datavalue.${key}.other_change`}
                          render={({ field, fieldState }) => (
                            <NumberInput 
                              {...field}
                              decimalSeparator="." 
                              thousandSeparator=","
                              className={`${inputClass} w-[150px]`} 
                              placeholder="-"
                              onValueChange={(e) => field.onChange(e.floatValue)}
                              error={fieldState.error?.message}
                              disabled={isSellingDisable}
                            />
                          )}
                        />
                      </td>
                      
                      {/* PRODUCT SELLING UNIT */}
                      <td className={borderTH}>
                        <Controller
                          control={methods.control}
                          name={`datavalue.${key}.unit_sell_measure_id`}
                          render={({ field, fieldState }) => (
                            <div className="flex items-top">
                              <Select 
                                controlClass={`${inputClass} w-[200px] !rounded-r-none`} 
                                placeholder="Select Item Unit"
                                isLoading={productWeight?.isLoadingUnit}
                                options={productWeight?.dataUnit}
                                value={field.value ? productWeight?.dataUnit.find((item) => item.value?.toString() === field.value?.toString()) : null}
                                onChange={(e) => field.onChange(e?.value)}
                                error={fieldState.error?.message}
                                isDisabled={isSellingDisable}
                              />
                              <Button
                                type="button"
                                color="primary"
                                className="!rounded-r-lg"
                                onClick={() => setModalConfig({ ...modalConfig, show: true, action: "WEIGHT" })}
                                disabled={isSellingDisable}
                              >
                                <FaPlus />
                              </Button>
                            </div>
                          )}
                        />
                      </td>

                      {/* QTY CONVENTION */}
                      <td className={borderTH}>
                        <Controller
                          control={methods.control}
                          name={`datavalue.${key}.quantity_unit_sell_convention`}
                          render={({ field, fieldState }) => (
                            <div className='flex'>
                              <NumberInput 
                                {...field}
                                decimalSeparator="." 
                                thousandSeparator=","
                                className={`${inputClass} w-[150px]`} 
                                placeholder="-"
                                onValueChange={(e) => field.onChange(e.floatValue)}
                                error={fieldState.error?.message}
                                disabled={isSellingDisable}
                              />
                              <Input 
                                value={buyUnitConvention ? productWeight?.dataUnit.find((item) => item.value?.toString() === buyUnitConvention?.toString())?.label : "-"}
                                className={`${inputClass} w-[150px]`} 
                                disabled 
                              />
                            </div>
                          )}
                        />
                      </td>

                      {/* RETAIL */}
                      <td className={borderTH}>
                        <Controller
                          control={methods.control}
                          name={`datavalue.${key}.price_retail`}
                          render={({ field, fieldState }) => (
                            <NumberInput 
                              {...field}
                              decimalSeparator="." 
                              thousandSeparator=","
                              className={`${inputClass} w-[100px]`} 
                              placeholder="-"
                              onValueChange={(e) => field.onChange(e.floatValue)}
                              error={fieldState.error?.message}
                              disabled={isSellingDisable}
                            />
                          )}
                        />
                      </td>

                      {/* WHOSALE */}
                      <td className={borderTH}>
                        <Controller
                          control={methods.control}
                          name={`datavalue.${key}.wholesale_price`}
                          render={({ field, fieldState }) => (
                            <NumberInput 
                              {...field}
                              decimalSeparator="." 
                              thousandSeparator=","
                              className={`${inputClass} w-[100px]`} 
                              placeholder="-"
                              onValueChange={(e) => field.onChange(e.floatValue)}
                              error={fieldState.error?.message}
                              disabled={isSellingDisable}
                            />
                          )}
                        />
                      </td>

                      {/* DISTRIBUTOR */}
                      <td className={borderTH}>
                        <Controller
                          control={methods.control}
                          name={`datavalue.${key}.distributor_price`}
                          render={({ field, fieldState }) => (
                            <NumberInput 
                              {...field}
                              decimalSeparator="." 
                              thousandSeparator=","
                              className={`${inputClass} w-[100px]`} 
                              placeholder="-"
                              onValueChange={(e) => field.onChange(e.floatValue)}
                              error={fieldState.error?.message}
                              disabled={isSellingDisable}
                            />
                          )}
                        />
                      </td>

                      {/* FACTORY */}
                      <td className={borderTH}>
                        <Controller
                          control={methods.control}
                          name={`datavalue.${key}.factory_price`}
                          render={({ field, fieldState }) => (
                            <NumberInput 
                              {...field}
                              decimalSeparator="." 
                              thousandSeparator=","
                              className={`${inputClass} w-[100px]`} 
                              placeholder="-"
                              onValueChange={(e) => field.onChange(e.floatValue)}
                              error={fieldState.error?.message}
                              disabled={isSellingDisable}
                            />
                          )}
                        />
                      </td>

                      {/* ONLINE */}
                      <td className={borderTH}>
                        <Controller
                          control={methods.control}
                          name={`datavalue.${key}.online_price`}
                          render={({ field, fieldState }) => (
                            <NumberInput 
                              {...field}
                              decimalSeparator="." 
                              thousandSeparator=","
                              className={`${inputClass} w-[100px]`} 
                              placeholder="-"
                              onValueChange={(e) => field.onChange(e.floatValue)}
                              error={fieldState.error?.message}
                              disabled={isSellingDisable}
                            />
                          )}
                        />
                      </td>

                      {/* SPECIAL ARANGEMEN */}
                      <td className={borderTH}>
                        <Controller
                          control={methods.control}
                          name={`datavalue.${key}.special_arrangement`}
                          render={({ field, fieldState }) => (
                            <NumberInput 
                              {...field}
                              decimalSeparator="." 
                              thousandSeparator=","
                              className={`${inputClass} w-[100px]`} 
                              placeholder="-"
                              onValueChange={(e) => field.onChange(e.floatValue)}
                              error={fieldState.error?.message}
                              disabled={isSellingDisable}
                            />
                          )}
                        />
                      </td>

                      {/* TAX WHEN SOLD */}
                      <td className={borderTH}>
                        <Controller
                          control={methods.control}
                          name={`datavalue.${key}.sell_tax_id`}
                          render={({ field, fieldState }) => (
                            <Select 
                              controlClass={`${inputClass} w-[200px]`} 
                              placeholder="Select Item Tax"
                              isLoading={productTax?.isLoadingTax}
                              options={productTax?.dataTax}
                              value={field.value ? productTax?.dataTax.find((item) => item.value?.toString() === field.value?.toString()) : null}
                              onChange={(e) => field.onChange(e?.value)}
                              error={fieldState.error?.message}
                              isDisabled={isSellingDisable}
                            />
                          )}
                        />
                      </td>
                      <td className={borderTH}>{Number(status) === StatusEnum.Review ? "Review" : "Submit"}</td>
                      {dataPage?.status === 1 && <td className={borderTH}>
                        <div className='flex justify-center gap-3 items-center'>
                          {Number(status) === StatusEnum.Review && <ActionButton.Delete onClick={() => handleDeleteSingle(previewId)} />}
                          <FaRegSave 
                            className={`h-[20px] w-[20px] cursor-pointer ${isProfileDisable ? "text-gray-400" : "" }`} 
                            onClick={() => (!isProfileDisable) && handleUpdateSingle(idexValue)} 
                          />

                        </div>
                      </td>}
                      {dataPage?.status === 1 && <td className={borderTH}>
                        <div className='flex justify-center gap-3 items-center'>
                          <input 
                            type='checkbox'
                            className='w-[22px] h-[22px] checkbox checkbox-primary'
                            checked={checkedIndexes.includes(key)}
                            onChange={() => handleCheckboxChange(key)}
                          >
                            
                          </input>
                        </div>
                      </td>}
                    </tr>
                  )
                })
              }
            </tbody>
          </BaseTable>
        </div>
      </FormProvider>
      <div className="mt-5 flex justify-between">
        <div className='w-[50%] mt-[-20px]'>
          <PaginationData data={dataSingle?.data} pagination={pagination} setPagination={setPagination} />
        </div>
        <div className="flex justify-end">
          <div className="flex gap-3">
            <Button color="error" onClick={() =>  navigate(-1)}>CANCEL</Button>
            {dataPage?.status === 1 && <Button color="primary" onClick={handleCreateSingle}>SUBMIT {`${selectedData?.length !== 0 ? "ITEMS" : ""}`}</Button>}
          </div>
        </div>
      </div>

      {/* POPUP GROUP */}
      {modalConfig?.show && modalConfig?.action === "GROUP" && (
        <PopupGroup
          toggle={() => {
            setModalConfig({ ...modalConfig, show: false, action: "" })
            productGroup?.refetchGroup()
          }}
        />
      )}

      {/* POPUP STYLE */}
      {modalConfig?.show && modalConfig?.action === "STYLE" && (
        <PopupStyle
          toggle={() => {
            setModalConfig({ ...modalConfig, show: false, action: "" })
            productStyle?.refetchStyle()
          }}
        />
      )}

      {/* POPUP SIZE */}
      {modalConfig?.show && modalConfig?.action === "SIZE" && (
        <PopupSize
          toggle={() => {
            setModalConfig({ ...modalConfig, show: false, action: "" })
            productSize?.refetchSize()
          }}
        />
      )}

      {/* POPUP UNIT */}
      {modalConfig?.show && modalConfig?.action === "WEIGHT" && (
        <PopupUnit
          toggle={() => {
            setModalConfig({ ...modalConfig, show: false, action: "" })
            productWeight?.refetchUnit()
          }}
        />
      )}

      {/* POPUP COLOUR */}
      {modalConfig?.show && modalConfig?.action === "COLOUR" && (
        <PopupColour
          toggle={() => {
            setModalConfig({ ...modalConfig, show: false, action: "" })
            productColour?.refetchColour()
          }}
        />
      )}

      {/* POPUP CATEGORY */}
      {modalConfig?.show && modalConfig?.action === "CATEGORY" && (
        <PopupCategory
          toggle={() => {
            setModalConfig({ ...modalConfig, show: false, action: "" })
            productCategory?.refetchCategory()
          }}
        />
      )}

      {/* POPUP MARKET */}
      {modalConfig?.show && modalConfig?.action === "MARKET" && (
        <PopupCustomer
          toggle={() => {
            setModalConfig({ ...modalConfig, show: false, action: "" })
            productMarket?.refetchMarket()
          }}
        />
      )}

      {/* POPUP MATERIAL */}
      {modalConfig?.show && modalConfig?.action === "MATERIAL" && (
        <PopupMaterial
          toggle={() => {
            setModalConfig({ ...modalConfig, show: false, action: "" })
            productMaterial?.refetchMaterial()
          }}
        />
      )}

      {/* POPUP I SELL */}
      {modalConfig?.show && modalConfig?.action === "SELL" && (
        <AccountModal
            defaultGroup="04"
            onAddSuccess={productSell?.refetch}
            toggle={() => setModalConfig({ ...modalConfig, show: false, action: "" })}
            onChange={() => {}}
        />
      )}

      {/* POPUP I INVENTORY */}
      {modalConfig?.show && modalConfig?.action === "INVENTORY" && (
        <AccountModal
            defaultGroup="01"
            onAddSuccess={productInventory?.refetch}
            toggle={() => setModalConfig({ ...modalConfig, show: false, action: "" })}
            onChange={() => {}}
        />
      )}

      {/* POPUP VENDOR */}
      {modalConfig?.show && modalConfig?.action === "VENDOR" && (
        <Modal
          isOpen 
          title="CREATE NEW VENDOR"
          size="full" 
          closeModal={() => {
            setModalConfig({ ...modalConfig, show: false, action: "" })
            productVendor.refetchVendor()
          }}
        >
          <PopupVendor asModal />
        </Modal>
      )}
    </section>
  )
}

type Payload = Record<string, any> | any[];